import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Select from "react-select";
import axios from "axios";
import imageCompression from "browser-image-compression";
import "./arena.css";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "react-toastify";
import { validateEmail, validatePassword } from "../../utils/passwordValidation";
import { useInstituteAuth } from "../../context/InstituteAuthContext";

// Leaflet icon setup
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const containerStyle = {
  width: "100%",
  height: "300px",
};

const center = [33.6844, 73.0479]; // Islamabad, Pakistan coordinates

function SetViewOnClick({ coords, shouldPan }) {
  const map = useMap();
  useEffect(() => {
    if (shouldPan && coords[0] !== 0 && coords[1] !== 0) {
      map.setView(coords, 13);
    }
  }, [coords, map, shouldPan]);
  return null;
}

const RegisterArena = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { context } = useParams();
  const { instituteUser } = useInstituteAuth();

  // Check if coming from institute context
  const isFromInstitute = context === "institute";
  const instituteId = isFromInstitute && instituteUser ? instituteUser.id : null;

  const [formData, setFormData] = useState({
    name: "",
    selectedSports: [""],
    date_established: "",
    email: "",
    password: "",
    address: "",
    city: "",
    country: "",
    area: "",
    street: "",
    houseNo: "",
    coordinates: "",
    phone: "", // Changed from array to single string
    contact_persons: [
      {
        first_name: "",
        middle_name: "",
        last_name: "",
        phone_no: "",
        email: "",
      },
    ],
    website: "",
    business_registration_no: "",
    tax_no: "",
    licenses: [{ title: "", reference_number: "" }],
    about_facility: "",
    logo: null,
    arena_photos: [],
    company_profile_photos: [],
    facebook: "",
    twitter: "",
    instagram: "",
    terms_and_conditions: false,
  });

  const [sportsOptions, setSportsOptions] = useState([]);
  const [selectedSports, setSelectedSports] = useState([]);
  const [facilities, setFacilities] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const [arenaPhotosPreview, setArenaPhotosPreview] = useState([]);
  const [companyProfilePhotosPreview, setCompanyProfilePhotosPreview] =
    useState([]);

  // New state for map
  const [mapCenter, setMapCenter] = useState(center);
  const [marker, setMarker] = useState(null);
  const [error, setError] = useState("");
  const [shouldPan, setShouldPan] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const mapRef = useRef(null);

  // Validation functions
  const validateRequiredField = (value, fieldName) => {
    if (!value || value.trim() === "") {
      return `${fieldName} is required`;
    }
    return "";
  };

  const validatePhoneNumber = (phone) => {
    if (!phone) return "Phone number is required";
    // Accept both mobile (03XXX-XXXXXXX) and landline (0XX-XXXXXXX) formats
    const mobileRegex = /^0\d{3}-\d{7}$/; // 03XXX-XXXXXXX
    const landlineRegex = /^0\d{2,3}-\d{7}$/; // 0XX-XXXXXXX
    if (!mobileRegex.test(phone) && !landlineRegex.test(phone)) {
      return "Phone number must be in format 03XXX-XXXXXXX or 0XX-XXXXXXX";
    }
    return "";
  };

  const validateAtLeastOneContactPerson = (contactPersons) => {
    if (!contactPersons || contactPersons.length === 0) {
      return "At least one contact person is required";
    }

    const hasValidContact = contactPersons.some(person =>
      person.first_name.trim() !== "" &&
      person.last_name.trim() !== "" &&
      person.phone_no.trim() !== ""
    );

    return hasValidContact ? "" : "At least one contact person must have first name, last name, and phone number (format: 03XXX-XXXXXXX or 0XX-XXXXXXX)";
  };

  const validateAtLeastOneFacility = (selectedSports, facilities) => {
    if (!selectedSports || selectedSports.length === 0) {
      return "At least one sport facility is required";
    }
    return "";
  };

  const validateFacilityDetails = (selectedSports, facilities) => {
    if (!selectedSports || selectedSports.length === 0) return "";

    for (const sport of selectedSports) {
      const facility = facilities[sport.value];
      if (!facility) {
        return `${sport.label}: Facility data is missing`;
      }

      if (!facility.facility_detail || facility.facility_detail.trim() === "") {
        return `${sport.label}: Facility detail is required`;
      }

      if (!facility.rate || facility.rate.trim() === "" || isNaN(facility.rate) || parseFloat(facility.rate) <= 0) {
        return `${sport.label}: Rate is required and must be a valid positive number (for 1 hour slot)`;
      }
    }

    return "";
  };

  const validateAtLeastOnePhoto = (arenaPhotos, companyProfilePhotos) => {
    // Photos are optional, so no validation needed
    return "";
  };

  useEffect(() => {
    const fetchSportsOptions = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/common/get-sports`,
          { withCredentials: true }
        );

        const formattedOptions = response.data
          .filter((sport) => sport.value !== "General")
          .map((sport) => ({
            value: sport.value,
            label: sport.value,
          }));

        setSportsOptions(formattedOptions);
      } catch (error) {
        console.error("Error fetching sports options:", error);
      }
    };

    fetchSportsOptions();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    const MAX_SIZE_BYTES = 400 * 1024; // 400KB target
    const MAX_ALLOWED_SIZE = 10 * 1024 * 1024; // 10MB limit

    // Check file size (10MB limit)
    if (file && file.size > MAX_ALLOWED_SIZE) {
      toast.error(
        "Logo image size must be less than 10MB. Please choose a smaller file."
      );
      e.target.value = ""; // Reset file input
      return;
    }

    if (!file) return;

    try {
      let processedFile = file;

      if (file.size > MAX_SIZE_BYTES) {
        console.log(
          `Compressing logo from ${(file.size / 1024).toFixed(1)}KB...`
        );

        const originalSizeKB = file.size / 1024;

        if (originalSizeKB <= 1000) {
          // For images 400KB - 1MB: Light compression
          processedFile = await imageCompression(file, {
            maxSizeMB: 0.38, // 380KB target
            maxWidthOrHeight: 1920,
            useWebWorker: true,
            fileType: "image/jpeg",
            initialQuality: 0.85,
          });
        } else if (originalSizeKB <= 3000) {
          // For images 1MB - 3MB: Moderate compression
          processedFile = await imageCompression(file, {
            maxSizeMB: 0.35, // 350KB target
            maxWidthOrHeight: 1600,
            useWebWorker: true,
            fileType: "image/jpeg",
            initialQuality: 0.75,
          });
        } else {
          // For images 3MB+: More aggressive compression
          processedFile = await imageCompression(file, {
            maxSizeMB: 0.32, // 320KB target
            maxWidthOrHeight: 1400,
            useWebWorker: true,
            fileType: "image/jpeg",
            initialQuality: 0.65,
          });
        }

        // Stage 2: If still too large, compress more
        if (processedFile.size > MAX_SIZE_BYTES) {
          console.log(
            `Stage 2: First compression result ${(
              processedFile.size / 1024
            ).toFixed(1)}KB, compressing more...`
          );
          processedFile = await imageCompression(processedFile, {
            maxSizeMB: 0.35,
            maxWidthOrHeight: 1200,
            useWebWorker: true,
            fileType: "image/jpeg",
            initialQuality: 0.6,
          });
        }

        // Stage 3: Final compression if still needed
        if (processedFile.size > MAX_SIZE_BYTES) {
          console.log(
            `Stage 3: Second compression result ${(
              processedFile.size / 1024
            ).toFixed(1)}KB, final compression...`
          );
          processedFile = await imageCompression(processedFile, {
            maxSizeMB: 0.38,
            maxWidthOrHeight: 1000,
            useWebWorker: true,
            fileType: "image/jpeg",
            initialQuality: 0.5,
          });
        }

        console.log(
          `Final logo size: ${(processedFile.size / 1024).toFixed(
            1
          )}KB (from ${(file.size / 1024).toFixed(1)}KB)`
        );

        if (processedFile.size > MAX_SIZE_BYTES) {
          console.warn(
            `Warning: Logo could not be compressed below 400KB. Final size: ${(
              processedFile.size / 1024
            ).toFixed(1)}KB`
          );
          toast.warning(
            `Logo could not be compressed below 400KB. Final size: ${(
              processedFile.size / 1024
            ).toFixed(1)}KB. It may affect performance.`
          );
        }
      } else {
        console.log(
          `Logo is already small enough: ${(file.size / 1024).toFixed(
            1
          )}KB - using original`
        );
      }

      setFormData({ ...formData, logo: processedFile });

      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(processedFile);
    } catch (error) {
      console.error("Logo compression failed:", error);
      toast.error("Failed to process logo image. Please try a different image.");
      e.target.value = "";
    }
  };

  const handleSportsChange = (selectedOptions) => {
    const selected = selectedOptions || [];
    setSelectedSports(selected);

    const newFacilities = {};
    selected.forEach((sport) => {
      newFacilities[sport.value] = {
        facility_detail: "",
        seating_capacity: "",
        standing_capacity: "",
        rate: "",
        images: [],
        imagePreviews: [],
      };
    });
    setFacilities(newFacilities);
  };

  const handleFacilityChange = (sport, field, value) => {
    const newFacilities = { ...facilities };
    newFacilities[sport][field] = value;
    setFacilities(newFacilities);
  };

  const handleFacilityImageChange = async (sport, e) => {
    const files = Array.from(e.target.files);
    const currentImages = facilities[sport]?.images || [];
    const MAX_SIZE_BYTES = 400 * 1024; // 400KB target
    const MAX_ALLOWED_SIZE = 10 * 1024 * 1024; // 10MB limit

    // Check if adding these files would exceed the 3 image limit
    if (currentImages.length + files.length > 3) {
      toast.error(
        `You can only upload a maximum of 3 images per facility. Currently you have ${currentImages.length} images for ${sport}.`
      );
      e.target.value = ""; // Reset file input
      return;
    }

    // Check for files exceeding 10MB limit
    const oversizedFiles = files.filter((file) => file.size > MAX_ALLOWED_SIZE);
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map((file) => file.name).join(", ");
      toast.error(
        `The following files exceed the maximum allowed size of 10MB: ${fileNames}. Please select smaller files.`
      );
      e.target.value = "";
      return;
    }

    const newFacilities = { ...facilities };

    // Process each file with compression and convert to base64
    const processFiles = async () => {
      for (const file of files) {
        try {
          let processedFile = file;

          if (file.size > MAX_SIZE_BYTES) {
            console.log(
              `Compressing facility image ${file.name} from ${(
                file.size / 1024
              ).toFixed(1)}KB...`
            );

            const originalSizeKB = file.size / 1024;

            if (originalSizeKB <= 1000) {
              // For images 400KB - 1MB: Light compression
              processedFile = await imageCompression(file, {
                maxSizeMB: 0.38, // 380KB target
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                fileType: "image/jpeg",
                initialQuality: 0.85,
              });
            } else if (originalSizeKB <= 3000) {
              // For images 1MB - 3MB: Moderate compression
              processedFile = await imageCompression(file, {
                maxSizeMB: 0.35, // 350KB target
                maxWidthOrHeight: 1600,
                useWebWorker: true,
                fileType: "image/jpeg",
                initialQuality: 0.75,
              });
            } else {
              // For images 3MB+: More aggressive compression
              processedFile = await imageCompression(file, {
                maxSizeMB: 0.32, // 320KB target
                maxWidthOrHeight: 1400,
                useWebWorker: true,
                fileType: "image/jpeg",
                initialQuality: 0.65,
              });
            }

            // Stage 2: If still too large, compress more
            if (processedFile.size > MAX_SIZE_BYTES) {
              console.log(
                `Stage 2: First compression result ${(
                  processedFile.size / 1024
                ).toFixed(1)}KB, compressing more...`
              );
              processedFile = await imageCompression(processedFile, {
                maxSizeMB: 0.35,
                maxWidthOrHeight: 1200,
                useWebWorker: true,
                fileType: "image/jpeg",
                initialQuality: 0.6,
              });
            }

            // Stage 3: Final compression if still needed
            if (processedFile.size > MAX_SIZE_BYTES) {
              console.log(
                `Stage 3: Second compression result ${(
                  processedFile.size / 1024
                ).toFixed(1)}KB, final compression...`
              );
              processedFile = await imageCompression(processedFile, {
                maxSizeMB: 0.38,
                maxWidthOrHeight: 1000,
                useWebWorker: true,
                fileType: "image/jpeg",
                initialQuality: 0.5,
              });
            }

            console.log(
              `Final facility image size: ${(processedFile.size / 1024).toFixed(
                1
              )}KB (from ${(file.size / 1024).toFixed(1)}KB)`
            );

            if (processedFile.size > MAX_SIZE_BYTES) {
              console.warn(
                `Warning: Image ${
                  file.name
                } could not be compressed below 400KB. Final size: ${(
                  processedFile.size / 1024
                ).toFixed(1)}KB`
              );
              toast.warning(
                `Image "${
                  file.name
                }" could not be compressed below 400KB. Final size: ${(
                  processedFile.size / 1024
                ).toFixed(1)}KB. It may affect performance.`
              );
            }
          } else {
            console.log(
              `Facility image ${file.name} is already small enough: ${(
                file.size / 1024
              ).toFixed(1)}KB - using original`
            );
          }

          const base64String = await fileToBase64(processedFile);
          newFacilities[sport].images.push(base64String);
          newFacilities[sport].imagePreviews.push(base64String);
        } catch (error) {
          console.error("Error processing facility image:", error);
          toast.error(`Error processing file ${file.name}. Please try again.`);
          continue;
        }
      }
      setFacilities({ ...newFacilities });
    };

    processFiles();
  };

  const removeFacilityImage = (sport, index) => {
    const newFacilities = { ...facilities };
    newFacilities[sport].images.splice(index, 1);
    newFacilities[sport].imagePreviews.splice(index, 1);
    setFacilities({ ...newFacilities });
  };

  const removeLogoImage = () => {
    setFormData({ ...formData, logo: null });
    setLogoPreview(null);
  };

  const removeArenaPhoto = (index) => {
    const newPhotos = formData.arena_photos.filter((_, i) => i !== index);
    const newPreviews = arenaPhotosPreview.filter((_, i) => i !== index);
    setFormData({ ...formData, arena_photos: newPhotos });
    setArenaPhotosPreview(newPreviews);
  };

  const removeCompanyPhoto = (index) => {
    const newPhotos = formData.company_profile_photos.filter(
      (_, i) => i !== index
    );
    const newPreviews = companyProfilePhotosPreview.filter(
      (_, i) => i !== index
    );
    setFormData({ ...formData, company_profile_photos: newPhotos });
    setCompanyProfilePhotosPreview(newPreviews);
  };

  const handlePhoneChange = (value) => {
    setFormData({ ...formData, phone: value });
  };

  const handleContactPersonChange = (index, field, value) => {
    const newContactPersons = [...formData.contact_persons];
    newContactPersons[index][field] = value;
    setFormData({ ...formData, contact_persons: newContactPersons });
  };

  const addContactPerson = () => {
    setFormData({
      ...formData,
      contact_persons: [
        ...formData.contact_persons,
        {
          first_name: "",
          middle_name: "",
          last_name: "",
          phone_no: "",
          email: "",
        },
      ],
    });
  };

  const removeContactPerson = (index) => {
    if (formData.contact_persons.length > 1) {
      const newContactPersons = formData.contact_persons.filter(
        (_, i) => i !== index
      );
      setFormData({ ...formData, contact_persons: newContactPersons });
    }
  };

  // License handling functions
  const handleLicenseChange = (index, field, value) => {
    const newLicenses = [...formData.licenses];
    newLicenses[index][field] = value;
    setFormData({ ...formData, licenses: newLicenses });
  };

  const addLicense = () => {
    setFormData({
      ...formData,
      licenses: [...formData.licenses, { title: "", reference_number: "" }],
    });
  };

  const removeLicense = (index) => {
    if (formData.licenses.length > 1) {
      const newLicenses = formData.licenses.filter((_, i) => i !== index);
      setFormData({ ...formData, licenses: newLicenses });
    }
  };

  // Check if institute is logged in when accessed from institute path
  useEffect(() => {
    if (isFromInstitute && !instituteId) {
      toast.error("Please login as institute owner to register arena");
      navigate("/institute-owner");
    }
  }, [isFromInstitute, instituteId, navigate]);

  const handlePhotoUpload = async (e, field) => {
    const files = Array.from(e.target.files);
    const currentPhotos = formData[field] || [];
    const MAX_SIZE_BYTES = 400 * 1024; // 400KB target
    const MAX_ALLOWED_SIZE = 10 * 1024 * 1024; // 10MB limit

    // Check if adding these files would exceed the 5 image limit
    if (currentPhotos.length + files.length > 5) {
      toast.error(
        `You can only upload a maximum of 5 images for ${
          field === "arena_photos" ? "arena photos" : "company profile photos"
        }. Currently you have ${currentPhotos.length} images.`
      );
      e.target.value = ""; // Reset file input
      return;
    }

    // Check for files exceeding 10MB limit
    const oversizedFiles = files.filter((file) => file.size > MAX_ALLOWED_SIZE);
    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map((file) => file.name).join(", ");
      toast.error(
        `The following files exceed the maximum allowed size of 10MB: ${fileNames}. Please select smaller files.`
      );
      e.target.value = "";
      return;
    }

    const processedPhotos = [];
    const newPreviews = [];

    for (const file of files) {
      try {
        let processedFile = file;

        if (file.size > MAX_SIZE_BYTES) {
          console.log(
            `Compressing ${field} image ${file.name} from ${(
              file.size / 1024
            ).toFixed(1)}KB...`
          );

          const originalSizeKB = file.size / 1024;

          if (originalSizeKB <= 1000) {
            // For images 400KB - 1MB: Light compression
            processedFile = await imageCompression(file, {
              maxSizeMB: 0.38, // 380KB target
              maxWidthOrHeight: 1920,
              useWebWorker: true,
              fileType: "image/jpeg",
              initialQuality: 0.85,
            });
          } else if (originalSizeKB <= 3000) {
            // For images 1MB - 3MB: Moderate compression
            processedFile = await imageCompression(file, {
              maxSizeMB: 0.35, // 350KB target
              maxWidthOrHeight: 1600,
              useWebWorker: true,
              fileType: "image/jpeg",
              initialQuality: 0.75,
            });
          } else {
            // For images 3MB+: More aggressive compression
            processedFile = await imageCompression(file, {
              maxSizeMB: 0.32, // 320KB target
              maxWidthOrHeight: 1400,
              useWebWorker: true,
              fileType: "image/jpeg",
              initialQuality: 0.65,
            });
          }

          // Stage 2: If still too large, compress more
          if (processedFile.size > MAX_SIZE_BYTES) {
            console.log(
              `Stage 2: First compression result ${(
                processedFile.size / 1024
              ).toFixed(1)}KB, compressing more...`
            );
            processedFile = await imageCompression(processedFile, {
              maxSizeMB: 0.35,
              maxWidthOrHeight: 1200,
              useWebWorker: true,
              fileType: "image/jpeg",
              initialQuality: 0.6,
            });
          }

          // Stage 3: Final compression if still needed
          if (processedFile.size > MAX_SIZE_BYTES) {
            console.log(
              `Stage 3: Second compression result ${(
                processedFile.size / 1024
              ).toFixed(1)}KB, final compression...`
            );
            processedFile = await imageCompression(processedFile, {
              maxSizeMB: 0.38,
              maxWidthOrHeight: 1000,
              useWebWorker: true,
              fileType: "image/jpeg",
              initialQuality: 0.5,
            });
          }

          console.log(
            `Final ${field} image size: ${(processedFile.size / 1024).toFixed(
              1
            )}KB (from ${(file.size / 1024).toFixed(1)}KB)`
          );

          if (processedFile.size > MAX_SIZE_BYTES) {
            console.warn(
              `Warning: Image ${
                file.name
              } could not be compressed below 400KB. Final size: ${(
                processedFile.size / 1024
              ).toFixed(1)}KB`
            );
            toast.warning(
              `Image "${
                file.name
              }" could not be compressed below 400KB. Final size: ${(
                processedFile.size / 1024
              ).toFixed(1)}KB. It may affect performance.`
            );
          }
        } else {
          console.log(
            `${field} image ${file.name} is already small enough: ${(
              file.size / 1024
            ).toFixed(1)}KB - using original`
          );
        }

        processedPhotos.push(processedFile);

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews.push(reader.result);

          // Update previews when all files are processed
          if (newPreviews.length === files.length) {
            if (field === "arena_photos") {
              setArenaPhotosPreview((prev) => [...prev, ...newPreviews]);
            } else if (field === "company_profile_photos") {
              setCompanyProfilePhotosPreview((prev) => [
                ...prev,
                ...newPreviews,
              ]);
            }
          }
        };
        reader.readAsDataURL(processedFile);
      } catch (error) {
        console.error(`Error processing ${field} image:`, error);
        toast.error(
          `Failed to process image "${file.name}". Please try a different image.`
        );
        continue;
      }
    }

    setFormData((prevFormData) => ({
      ...prevFormData,
      [field]: [...prevFormData[field], ...processedPhotos],
    }));
  };

  // New functions for address handling
  const geocodeAddress = useCallback(async () => {
    const { country, city, area, street, houseNo } = formData;
    const address = `${houseNo} ${street}, ${area}, ${city}, ${country}`.trim();
    if (!address) return;

    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/search`,
        {
          params: {
            q: address,
            format: "json",
            limit: 1,
            addressdetails: 1,
          },
        }
      );

      if (response.data.length > 0) {
        const { lat, lon, address: details } = response.data[0];
        setMapCenter([parseFloat(lat), parseFloat(lon)]);
        setMarker([parseFloat(lat), parseFloat(lon)]);
        setShouldPan(true);
        setFormData((prevData) => ({
          ...prevData,
          country: details.country || prevData.country,
          city: details.city || details.town || prevData.city,
          area: details.suburb || prevData.area,
          street: details.road || prevData.street,
          houseNo: details.house_number || prevData.houseNo,
          coordinates: `${lat}, ${lon}`,
        }));
        setError("");
      } else {
        setError("No location found for this address");
      }
    } catch (error) {
      console.error("Error geocoding:", error);
      setError("Error finding location");
    }
  }, [formData]);

  const handleMapClick = useCallback(async (e) => {
    const { lat, lng } = e.latlng;
    setMarker([lat, lng]);
    setMapCenter([lat, lng]);
    setShouldPan(true);
    setFormData((prevData) => ({
      ...prevData,
      coordinates: `${lat}, ${lng}`,
    }));

    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse`,
        {
          params: {
            lat: lat,
            lon: lng,
            format: "json",
            addressdetails: 1,
          },
        }
      );

      if (response.data) {
        const { address } = response.data;
        setFormData((prevData) => ({
          ...prevData,
          country: address.country || "",
          city: address.city || address.town || "",
          area: address.suburb || address.neighbourhood || address.county || "",
          street: address.road || "",
          houseNo: address.house_number || "",
          coordinates: `${lat}, ${lng}`,
        }));
        setError("");
      } else {
        setError("No address found for this location");
      }
    } catch (error) {
      console.error("Error reverse geocoding:", error);
      setError("Error fetching address information");
    }
  }, []);

  const MapEvents = () => {
    useMapEvents({
      click: handleMapClick,
      dragstart: () => setShouldPan(false),
      zoomstart: () => setShouldPan(false),
    });
    return null;
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      if (!(file instanceof Blob)) {
        console.error("Invalid file object:", file);
        reject(new Error("Invalid file object"));
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    newErrors.name = validateRequiredField(formData.name, "Arena name");
    newErrors.email = formData.email ? validateEmail(formData.email) : "Email is required";
    newErrors.password = formData.password ? validatePassword(formData.password) : "Password is required";
    newErrors.date_established = validateRequiredField(formData.date_established, "Date of establishment");
    newErrors.about_facility = validateRequiredField(formData.about_facility, "About the arena");

    // Address validations (house number is optional)
    newErrors.city = validateRequiredField(formData.city, "City");
    newErrors.area = validateRequiredField(formData.area, "Area");
    newErrors.street = validateRequiredField(formData.street, "Street");
    // House number is optional - no validation needed

    // Phone validation
    newErrors.phone = validatePhoneNumber(formData.phone);

    // Contact person validation
    newErrors.contactPersons = validateAtLeastOneContactPerson(formData.contact_persons);

    // Sports and facilities validation
    newErrors.facilities = validateAtLeastOneFacility(selectedSports, facilities);
    newErrors.facilityDetails = validateFacilityDetails(selectedSports, facilities);

    // Photo validation
    newErrors.photos = validateAtLeastOnePhoto(formData.arena_photos, formData.company_profile_photos);

    // Terms validation
    if (!formData.terms_and_conditions) {
      newErrors.terms = "You must agree to the terms and conditions";
    }

    // Note: business_registration_no and tax_no are optional fields and are not validated

    // Validate contact persons
    for (let i = 0; i < formData.contact_persons.length; i++) {
      const person = formData.contact_persons[i];
      if (!person.first_name.trim()) {
        newErrors[`contactPerson_${i}_firstName`] = `Contact person ${i + 1}: First name is required`;
      }
      if (!person.last_name.trim()) {
        newErrors[`contactPerson_${i}_lastName`] = `Contact person ${i + 1}: Last name is required`;
      }
      if (person.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(person.email)) {
        newErrors[`contactPerson_${i}_email`] = `Contact person ${i + 1}: Valid email format required`;
      }
      if (!person.phone_no.trim()) {
        newErrors[`contactPerson_${i}_phone`] = `Contact person ${i + 1}: Phone number is required`;
      } else {
        const phoneError = validatePhoneNumber(person.phone_no);
        if (phoneError) {
          newErrors[`contactPerson_${i}_phone`] = `Contact person ${i + 1}: ${phoneError}`;
        }
      }
    }

    // Validate facilities
    for (const sport of selectedSports) {
      const facility = facilities[sport.value];
      if (!facility) continue;

      if (!facility.facility_detail || facility.facility_detail.trim() === "") {
        newErrors[`facility_${sport.value}_detail`] = `${sport.label}: Facility detail is required`;
      }

      if (!facility.rate || facility.rate.trim() === "" || isNaN(facility.rate) || parseFloat(facility.rate) <= 0) {
        newErrors[`facility_${sport.value}_rate`] = `${sport.label}: Rate is required and must be a valid positive number (for 1 hour slot)`;
      }

      if (facility.seating_capacity && (isNaN(facility.seating_capacity) || facility.seating_capacity < 0)) {
        newErrors[`facility_${sport.value}_seating`] = `${sport.label}: Seating capacity must be a valid number`;
      }

      if (facility.standing_capacity && (isNaN(facility.standing_capacity) || facility.standing_capacity < 0)) {
        newErrors[`facility_${sport.value}_standing`] = `${sport.label}: Standing capacity must be a valid number`;
      }

      // Validate facility images
      if (facility.images && facility.images.length > 3) {
        newErrors[`facility_${sport.value}_images`] = `${sport.label}: You can upload a maximum of 3 facility images`;
      }
    }

    // Validate logo size
    if (formData.logo && formData.logo.size > 10 * 1024 * 1024) {
      newErrors.logoSize = "Logo image size must be less than 10MB";
    }

    // Validate arena photos
    if (formData.arena_photos && formData.arena_photos.length > 5) {
      newErrors.arenaPhotosCount = "You can upload a maximum of 5 arena photos";
    }

    // Validate company profile photos
    if (formData.company_profile_photos && formData.company_profile_photos.length > 5) {
      newErrors.companyPhotosCount = "You can upload a maximum of 5 company profile photos";
    }

    setErrors(newErrors);

    // Check if there are any validation errors and display specific messages
    const hasErrors = Object.values(newErrors).some(error => error !== "");
    if (hasErrors) {
      // Debug: Log all errors to console for troubleshooting
      console.log("Validation errors found:", newErrors);

      // Display specific error messages for the first few errors
      const errorMessages = Object.values(newErrors).filter(error => error !== "");
      if (errorMessages.length > 0) {
        toast.error(`Please fix the following errors:\n${errorMessages.slice(0, 3).join('\n')}`);
      } else {
        toast.error("Please fix the errors in the form before submitting.");
      }
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Format selected sports
      const formattedSelectedSports = selectedSports.map((sport) => {
        if (typeof sport === "object" && sport.value) {
          return sport.value;
        }
        return sport;
      });

      // Convert File objects to base64 strings
      let logoBase64 = null;
      if (formData.logo && formData.logo instanceof File) {
        logoBase64 = await fileToBase64(formData.logo);
      }

      let arenaPhotosBase64 = [];
      if (Array.isArray(formData.arena_photos)) {
        arenaPhotosBase64 = await Promise.all(
          formData.arena_photos
            .filter((file) => file instanceof File)
            .map(fileToBase64)
        );
      }

      let companyProfilePhotosBase64 = [];
      if (Array.isArray(formData.company_profile_photos)) {
        companyProfilePhotosBase64 = await Promise.all(
          formData.company_profile_photos
            .filter((file) => file instanceof File)
            .map(fileToBase64)
        );
      }

      // Convert facility images - they're already base64 strings from handleFacilityImageChange
      const facilitiesData = {};
      for (const sport in facilities) {
        facilitiesData[sport] = {
          ...facilities[sport],
          // Images are already base64 strings, no need to convert
          images: facilities[sport].images || [],
        };
      }

      // Prepare data in the format expected by the backend
      const jsonData = {
        arena_name: formData.name,
        logo: logoBase64,
        telephone: formData.phone,
        email: formData.email,
        website: formData.website,
        business_registration_number: formData.business_registration_no,
        tax_payer_number: formData.tax_no,
        instagram: formData.instagram,
        facebook: formData.facebook,
        twitter: formData.twitter,
        date_established: formData.date_established,
        password: formData.password,
        about_facility: formData.about_facility,
        contacts: formData.contact_persons.map((person) => ({
          first_name: person.first_name,
          middle_name: person.middle_name,
          last_name: person.last_name,
          email: person.email,
          phone: person.phone_no,
        })),
        facilities: facilitiesData,
        licenses: formData.licenses.filter(
          (license) => license.title.trim() !== ""
        ),
        arena_photos: arenaPhotosBase64,
        company_profile_photos: companyProfilePhotosBase64,
        address: {
          country: formData.country,
          city: formData.city,
          area: formData.area,
          street: formData.street,
          plot_no: formData.houseNo,
          coordinates: formData.coordinates,
        },
        institute_id: instituteId, // Include institute_id if coming from institute
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/arena/register-arena`,
        jsonData,
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      if (response.data && response.data.success) {
        toast.success(response.data.message || "Arena registered successfully!");
        // Redirect based on context
        if (isFromInstitute) {
          navigate("/institute-owner");
        } else {
          navigate("/user-dashboard");
        }
      } else {
        throw new Error(response.data.message || "Failed to register arena");
      }
    } catch (error) {
      console.error("Error registering arena:", error);
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        // Show the actual server error message
        toast.error(`Registration failed: ${error.response.data.message}`);
      } else {
        toast.error(`Registration failed: ${error.message}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <img 
            src="/logo.png" 
            alt="Logo" 
            className="h-[50px] sm:h-[60px] mx-auto mb-4 sm:mb-6 cursor-pointer hover:opacity-80 transition-opacity drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]" 
            onClick={() => navigate(isFromInstitute ? "/institute-owner" : "/user-dashboard")}
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-[#3498db] mb-2">
            Register Your Arena
          </h1>
          <p className="text-[#57606f] mt-2 text-sm sm:text-base">
            Create account by filling the form below
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.05)] rounded-2xl p-4 sm:p-8 space-y-6 sm:space-y-8 backdrop-blur-[10px] border border-white/20">
          <div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              Basic Information
            </h2>
            <label className="block text-sm font-medium text-gray-700 mb-2">Name of the Arena <span className="text-red-500">*</span></label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 focus:outline-none ${
                errors.name 
                  ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]" 
                  : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
              }`}
            />
            {errors.name && (
              <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                <span className="text-xs">⚠</span>
                {errors.name}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sports Facilities <span className="text-red-500">*</span></label>
            <Select
              className="basic-multi-select"
              classNamePrefix="select"
              options={sportsOptions}
              isMulti
              onChange={handleSportsChange}
              required
            />
          </div>

          {selectedSports.map((sport) => (
            <div key={sport.value} className="border border-[#ddd] rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">{sport.label}</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Facility Detail <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={facilities[sport.value]?.facility_detail || ""}
                  onChange={(e) =>
                    handleFacilityChange(
                      sport.value,
                      "facility_detail",
                      e.target.value
                    )
                  }
                  required
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Seating Capacity <span className="text-gray-500">(Optional)</span></label>
                  <input
                    type="number"
                    min="0"
                    value={facilities[sport.value]?.seating_capacity || ""}
                    onChange={(e) =>
                      handleFacilityChange(
                        sport.value,
                        "seating_capacity",
                        e.target.value
                      )
                    }
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Standing Capacity <span className="text-gray-500">(Optional)</span></label>
                  <input
                    type="number"
                    min="0"
                    value={facilities[sport.value]?.standing_capacity || ""}
                    onChange={(e) =>
                      handleFacilityChange(
                        sport.value,
                        "standing_capacity",
                        e.target.value
                      )
                    }
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Rate <span className="text-red-500">*</span></label>
                <small className="text-gray-600 text-xs block mb-2">
                  Rate for 1 hour slot
                </small>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={facilities[sport.value]?.rate || ""}
                  onChange={(e) =>
                    handleFacilityChange(sport.value, "rate", e.target.value)
                  }
                  required
                  className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload Photos <span className="text-red-500">*</span></label>
                <small
                  style={{
                    color: "#666",
                    display: "block",
                    marginBottom: "5px",
                  }}
                >
                  Maximum 3 images, each up to 10MB. Images will be
                  automatically compressed to optimize performance. (
                  {facilities[sport.value].images.length}/3 uploaded)
                </small>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleFacilityImageChange(sport.value, e)}
                  required
                />
                <div className="image-preview">
                  {facilities[sport.value].imagePreviews.map((photo, index) => (
                    <div
                      key={index}
                      style={{
                        position: "relative",
                        display: "inline-block",
                        margin: "5px",
                      }}
                    >
                      <img
                        src={photo}
                        alt={`${sport.label} Preview ${index + 1}`}
                        style={{
                          width: "150px",
                          height: "100px",
                          objectFit: "cover",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeFacilityImage(sport.value, index)}
                        style={{
                          position: "absolute",
                          top: "5px",
                          right: "5px",
                          backgroundColor: "#ff4444",
                          color: "white",
                          border: "none",
                          borderRadius: "50%",
                          width: "25px",
                          height: "25px",
                          cursor: "pointer",
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date of Establishment <span className="text-red-500">*</span></label>
            <input
              type="date"
              name="date_established"
              value={formData.date_established}
              onChange={handleInputChange}
              required
              className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo <span className="text-gray-500">(Optional)</span>
            </label>
            <small className="text-gray-600 text-xs block mb-2">
              Maximum file size: 10MB. Images will be automatically compressed to optimize performance.
            </small>
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-32 h-32 border-2 border-gray-300 border-dashed rounded-full cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors overflow-hidden">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  style={{ display: "none" }}
                />
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Logo Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center">
                    <i className="fas fa-camera text-gray-500 text-2xl mb-2"></i>
                    <p className="text-sm text-gray-500">Upload Logo</p>
                  </div>
                )}
              </label>
            </div>
            {logoPreview && (
              <button
                type="button"
                onClick={removeLogoImage}
                className="mt-3 px-4 py-2 bg-[#dc3545] text-white rounded-lg hover:bg-[#c82333] transition-all duration-300 font-semibold text-sm"
              >
                Remove Logo
              </button>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password <span className="text-red-500">*</span></label>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                minLength="6"
                required
                className="w-full h-11 px-3 pr-12 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                placeholder="Enter a strong password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
            {/* Password strength indicator */}
            {formData.password && (
              <div style={{ marginTop: "5px", fontSize: "12px", color: "#666" }}>
                Password strength: {
                  formData.password.length < 6 ? "Too short" :
                  !/\d/.test(formData.password) ? "Add numbers" :
                  !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password) ? "Add special characters" :
                  "Strong"
                }
              </div>
            )}
          </div>

          {/* Address fields */}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Address Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
              <input
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                placeholder="Country *"
                className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
              />
              <input
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="City"
                className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
              />
              <input
                name="area"
                value={formData.area}
                onChange={handleInputChange}
                placeholder="Area *"
                className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
              />
              <input
                name="street"
                value={formData.street}
                onChange={handleInputChange}
                placeholder="Street *"
                className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
              />
              <input
                name="houseNo"
                value={formData.houseNo}
                onChange={handleInputChange}
                placeholder="House No"
                className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
              />
              <input
                name="coordinates"
                value={formData.coordinates}
                placeholder="Coordinates *"
                readOnly
                className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] bg-[#f8f9fa]"
              />
            </div>
            <button 
              type="button" 
              onClick={geocodeAddress}
              className="mb-4 px-4 py-2 h-11 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] focus:outline-none transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
            >
              Find Coordinates
            </button>
          </div>

          <MapContainer
            center={mapCenter}
            zoom={13}
            style={containerStyle}
            ref={mapRef}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {marker && <Marker position={marker} />}
            <SetViewOnClick coords={mapCenter} shouldPan={shouldPan} />
            <MapEvents />
          </MapContainer>

          <div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              Contact Information
            </h2>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number <span className="text-red-500">*</span></label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder="Phone Number * (e.g., 03XXX-XXXXXXX or 0XX-XXXXXXX)"
              pattern="[0-9\s\-\+\(\)]+"
              required
              className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">Contact Person Details</label>
            {formData.contact_persons.map((person, index) => (
              <div key={index} className="border border-[#ddd] rounded-lg p-3 sm:p-4 mb-4">
                <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Contact Info {index + 1}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <input
                    type="text"
                    name="first_name"
                    placeholder="First Name *"
                    value={person.first_name}
                    onChange={(e) =>
                      handleContactPersonChange(
                        index,
                        "first_name",
                        e.target.value
                      )
                    }
                    required
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                  <input
                    type="text"
                    name="middle_name"
                    placeholder="Middle Name"
                    value={person.middle_name}
                    onChange={(e) =>
                      handleContactPersonChange(
                        index,
                        "middle_name",
                        e.target.value
                      )
                    }
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                  <input
                    type="text"
                    name="last_name"
                    placeholder="Last Name"
                    value={person.last_name}
                    onChange={(e) =>
                      handleContactPersonChange(
                        index,
                        "last_name",
                        e.target.value
                      )
                    }
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={person.email}
                    onChange={(e) =>
                      handleContactPersonChange(index, "email", e.target.value)
                    }
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                  <input
                    type="tel"
                    name="phone_no"
                    placeholder="Phone No. * (e.g., 03XXX-XXXXXXX or 0XX-XXXXXXX)"
                    value={person.phone_no}
                    onChange={(e) =>
                      handleContactPersonChange(index, "phone_no", e.target.value)
                    }
                    pattern="[0-9\s\-\+\(\)]+"
                    required
                    className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
                  />
                </div>
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 h-10 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100"
                    onClick={addContactPerson}
                  >
                    +
                  </button>
                  {formData.contact_persons.length > 1 && (
                    <button
                      type="button"
                      className="px-4 py-2 h-10 bg-[#dc3545] text-white rounded-lg hover:bg-[#c82333] transition-all duration-300 font-semibold"
                      onClick={() => removeContactPerson(index)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
              Additional Information
            </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Website <span className="text-gray-500">(Optional)</span></label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Business Registration Number <span className="text-gray-500">(Optional)</span></label>
              <input
                type="text"
                name="business_registration_no"
                value={formData.business_registration_no}
                onChange={handleInputChange}
                className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tax Number <span className="text-gray-500">(Optional)</span></label>
              <input
                type="text"
                name="tax_no"
                value={formData.tax_no}
                onChange={handleInputChange}
                className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
              />
            </div>
          </div>

          {/* Licenses Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">Licenses <span className="text-gray-500">(Optional)</span></label>
            {formData.licenses.map((license, index) => (
              <div
                key={index}
                style={{
                  marginBottom: "15px",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                }}
              >
                <div
                  style={{ display: "flex", gap: "10px", marginBottom: "10px" }}
                >
                  <input
                    type="text"
                    placeholder="License Title"
                    value={license.title}
                    onChange={(e) =>
                      handleLicenseChange(index, "title", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                  <input
                    type="text"
                    placeholder="Reference Number"
                    value={license.reference_number}
                    onChange={(e) =>
                      handleLicenseChange(
                        index,
                        "reference_number",
                        e.target.value
                      )
                    }
                    style={{ flex: 1 }}
                  />
                </div>
                <div>
                  {index === formData.licenses.length - 1 && (
                    <button
                      type="button"
                      onClick={addLicense}
                      style={{
                        backgroundColor: "#28a745",
                        color: "white",
                        border: "none",
                        padding: "5px 10px",
                        borderRadius: "3px",
                        cursor: "pointer",
                        marginRight: "10px",
                      }}
                    >
                      + Add License
                    </button>
                  )}
                  {formData.licenses.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLicense(index)}
                      style={{
                        backgroundColor: "#dc3545",
                        color: "white",
                        border: "none",
                        padding: "5px 10px",
                        borderRadius: "3px",
                        cursor: "pointer",
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">About the Arena <span className="text-red-500">*</span></label>
            <textarea
              name="about_facility"
              value={formData.about_facility}
              onChange={handleInputChange}
              className="w-full px-3 py-2 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
              rows="4"
            ></textarea>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Upload Arena Photos <span className="text-gray-500">(Optional)</span></label>
            <small
              style={{ color: "#666", display: "block", marginBottom: "5px" }}
            >
              Maximum 5 images, each up to 10MB. Images will be automatically
              compressed to optimize performance. (
              {formData.arena_photos.length}
              /5 uploaded)
            </small>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handlePhotoUpload(e, "arena_photos")}
            />
            <div className="image-preview">
              {arenaPhotosPreview.map((photo, index) => (
                <div
                  key={index}
                  style={{
                    position: "relative",
                    display: "inline-block",
                    margin: "5px",
                  }}
                >
                  <img
                    src={photo}
                    alt={`Arena Preview ${index + 1}`}
                    style={{
                      width: "150px",
                      height: "100px",
                      objectFit: "cover",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => removeArenaPhoto(index)}
                    style={{
                      position: "absolute",
                      top: "5px",
                      right: "5px",
                      backgroundColor: "#ff4444",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "25px",
                      height: "25px",
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Upload Company Profile Photos <span className="text-gray-500">(Optional)</span></label>
            <small
              style={{ color: "#666", display: "block", marginBottom: "5px" }}
            >
              Maximum 5 images, each up to 10MB. Images will be automatically
              compressed to optimize performance. (
              {formData.company_profile_photos.length}/5 uploaded)
            </small>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handlePhotoUpload(e, "company_profile_photos")}
            />
            <div className="image-preview">
              {companyProfilePhotosPreview.map((photo, index) => (
                <div
                  key={index}
                  style={{
                    position: "relative",
                    display: "inline-block",
                    margin: "5px",
                  }}
                >
                  <img
                    src={photo}
                    alt={`Company Profile Preview ${index + 1}`}
                    style={{
                      width: "150px",
                      height: "100px",
                      objectFit: "cover",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => removeCompanyPhoto(index)}
                    style={{
                      position: "absolute",
                      top: "5px",
                      right: "5px",
                      backgroundColor: "#ff4444",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "25px",
                      height: "25px",
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Facebook <span className="text-gray-500">(Optional)</span></label>
              <input
                type="url"
                name="facebook"
                value={formData.facebook}
                onChange={handleInputChange}
                className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Twitter <span className="text-gray-500">(Optional)</span></label>
              <input
                type="url"
                name="twitter"
                value={formData.twitter}
                onChange={handleInputChange}
                className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Instagram <span className="text-gray-500">(Optional)</span></label>
              <input
                type="url"
                name="instagram"
                value={formData.instagram}
                onChange={handleInputChange}
                className="w-full h-11 px-3 rounded-lg text-base border border-[#ddd] transition-all duration-200 focus:outline-none focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
              />
            </div>
          </div>

          <div>
            <label>
              <input
                type="checkbox"
                name="terms_and_conditions"
                checked={formData.terms_and_conditions}
                onChange={handleInputChange}
                required
              />{" "}
              I agree to the terms and conditions *
            </label>
          </div>

          <button 
            type="submit" 
            className="w-full h-11 bg-[#3498db] text-white rounded-lg hover:bg-[#2980b9] transition-all duration-300 font-semibold uppercase tracking-[0.5px] shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100" 
            disabled={isSubmitting}
          >
            {isSubmitting ? "REGISTERING..." : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );


};

export default RegisterArena;
